/**
 * ShadowColumn — shadow lane: AI drafts + typing indicator.
 */

import { useRef, useEffect } from 'react';
import { TypingDots } from '../ui';
import type { DemoRound } from './DemoExperience';

interface ShadowColumnProps {
  rounds: DemoRound[];
}

export function ShadowColumn({ rounds }: ShadowColumnProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevKeyRef = useRef<string | null>(null);

  const lastProposalId = rounds[rounds.length - 1]?.proposal?.id ?? null;
  useEffect(() => {
    if (!lastProposalId || prevKeyRef.current === lastProposalId) return;
    prevKeyRef.current = lastProposalId;
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [rounds.length, lastProposalId]);

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border-subtle">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-accent-ai" />
          <span className="text-[10px] font-bold text-accent-ai uppercase tracking-wider">Shadow Draft</span>
        </div>
      </div>

      {/* Drafts */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {rounds.length === 0 && (
          <div className="flex-1 flex items-center justify-center h-32">
            <p className="text-xs text-text-muted text-center leading-relaxed">
              Здесь появится<br />черновик AI
            </p>
          </div>
        )}

        {rounds.map(round => {
          const proposal = round.proposal;

          // Thinking state
          if (!proposal || proposal.status === 'thinking') {
            return (
              <div key={round.clientMessage.id} className="space-y-2">
                <p className="text-xs text-text-muted truncate">
                  К: {round.clientMessage.text.slice(0, 60)}{round.clientMessage.text.length > 60 ? '...' : ''}
                </p>
                <div className="flex items-center gap-2 px-3 py-3 rounded-card bg-accent-ai-light/50 border border-accent-ai/10">
                  <span className="text-[10px] font-medium text-accent-ai">Anima думает</span>
                  <TypingDots />
                </div>
              </div>
            );
          }

          // Skipped
          if (proposal.status === 'skipped') {
            return (
              <div key={round.clientMessage.id} className="space-y-2">
                <p className="text-xs text-text-muted truncate">
                  К: {round.clientMessage.text.slice(0, 60)}{round.clientMessage.text.length > 60 ? '...' : ''}
                </p>
                <div className="px-3 py-3 rounded-card bg-surface-subtle border border-border-subtle">
                  <p className="text-xs text-text-secondary italic">
                    Этот вопрос за пределами моей базы знаний. Попробуйте спросить о возможностях, тарифах или подключении.
                  </p>
                </div>
              </div>
            );
          }

          // Ready / matched
          return (
            <div key={round.clientMessage.id} className="space-y-2">
              <p className="text-xs text-text-muted truncate">
                К: {round.clientMessage.text.slice(0, 60)}{round.clientMessage.text.length > 60 ? '...' : ''}
              </p>
              <div className="rounded-card bg-accent-ai-light/30 border border-accent-ai/15 p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-accent-ai-light text-accent-ai rounded-md">
                    Draft
                  </span>
                </div>
                <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">
                  {proposal.text}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
